// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ReactSVG } from "react-svg";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import CashTransferService from "../../cash-transfer.service";
import { WithdrawalGroups } from "../../../../utils/data/constant";
import CustomSearch from "src/components/custom-search/custom-search";

let searchTimer = null;

const Pending: React.FC = (): ReactElement => {
  const cashTransferService = new CashTransferService();
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const loadingState = useSelector((state: any) => state?.loading);
  const [pendingRequestsList, setPendingRequestsList] = useState<any>([]);
  const [responseDate, setResponseDate] = useState<any>({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchWord, setSearchWord] = useState(null);

  useEffect(() => {
    fetchPendingListData();
  }, [searchWord, sortOrder, sortField]);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 40,
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Client name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (_, record) => (
        <span>
          {record?.bankAccount?.beneficiaryName ||
            record?.account?.activeKyc?.nameEn}
        </span>
      ),
    },
    {
      title: "Account",
      dataIndex: "accountCode",
      key: "accountCode",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <Link
          to={`/home/business-account/${record?.account?.id}/${record?.account?.activeKycId}/details`}
        >
          {record?.account?.accountCode}
        </Link>
      ),
    },
    {
      title: "Wealth manager",
      dataIndex: "wealthManager",
      key: "wealthManager",
      render: (_, record) => record?.account?.wealthManager?.ae1 || "-",
    },
    {
      title: "From portfolio",
      dataIndex: "sourcePortfolio",
      key: "sourcePortfolio",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <span>{record?.sourcePortfolio?.accountCode}</span>
      ),
    },
    {
      title: "To portfolio",
      dataIndex: "targetPortfolio",
      key: "targetPortfolio",
      sorter: true,
      fixed: "left",
      render: (_, record) => (
        <span>{record?.targetPortfolio?.accountCode || "-"}</span>
      ),
    },
    {
      title: "Request type",
      dataIndex: "requestType",
      key: "requestType",
      render: (_, record) => (
        <span>
          {record?.targetPortfolio ? "Internal cash transfer" : "Fx conversion"}
        </span>
      ),
    },
    {
      title: "All cash",
      dataIndex: "transferAll",
      key: "transferAll",
      render: (text) => <span>{text ? "Yes" : "No"}</span>,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "souce",
      render: (text) =>
        text === "Portal" ? (
          <ReactSVG src={"/images/monitor.svg"} />
        ) : (
          <ReactSVG src={"/images/mobile.svg"} />
        ),
    },
    {
      title: "Request Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (text) => (
        <span className="date text-gray-500">
          {moment(text).format("DD MMM, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <ReactSVG
          src={"/images/take-action.svg"}
          onClick={() => {
            navigate(`/cash-transfer/take-action/${record?.id}`);
          }}
        />
      ),
    },
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    setSortField(sorter.field);
    setSortOrder(sorter.order);
  };

  const fetchPendingListData = async (page = 1): Promise<void> => {
    const data: any = await cashTransferService.getCashTransferListData(
      ["Pending"],
      page,
      searchWord,
      sortField,
      sortOrder
    );

    if (data !== undefined) {
      setResponseDate(data?.data);
      setPendingRequestsList(data?.data?.cashTransfers);
    }
  };

  const onSearch = (e) => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? pendingRequestsList?.length : ""} Pending requests{" "}
          {user?.withdrawGroup
            ? `for ${WithdrawalGroups[user?.withdrawGroup]}`
            : ""}
        </p>
        <div className=" flex items-center">
          <CustomSearch onChange={(event) => onSearch(event)} />
        </div>
      </div>
      {pendingRequestsList?.length ? (
        <CustomTable
          columns={columns}
          data={pendingRequestsList}
          showPagination={true}
          pageSize={responseDate?.pageSize}
          totalCount={responseDate?.totalCount}
          page={responseDate?.currentPage}
          handleTableChange={handleTableChange}
          onPageChange={(page) => {
            fetchPendingListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default Pending;
